"use client";

type CurrencyOptions = {
  currency?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

const numberFormatter = new Intl.NumberFormat("en-US");
const dateFormatterUTC = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  timeZone: "UTC",
});

export const formatNumber = (value: number) => numberFormatter.format(value);

export const formatDateUTC = (value: string | number | Date, throwOnInvalidDate = false) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    if (throwOnInvalidDate) {
      throw new Error(`Invalid date (cannot format): ${value}`);
    }
    return "";
  }
  return dateFormatterUTC.format(date);
};

export const formatDateShort = (value?: string | number | Date | null, fallback = "—") => {
  if (!value) {
    return fallback;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatLocalDateTime = (value: string | number | Date, fallback = "—") => {
  // Lighthouse's DateTime scalar emits "YYYY-MM-DD HH:MM:SS" without a timezone suffix.
  // The server is always UTC, so we append Z when no offset is present so the browser
  // treats it as UTC and converts correctly to local time.
  const normalized =
    typeof value === "string" && !/Z|[+-]\d{2}:?\d{2}$/.test(value) ? `${value.replace(" ", "T")}Z` : value;
  const date = normalized instanceof Date ? normalized : new Date(normalized);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("en-US", { dateStyle: "short", timeStyle: "short" }).format(date);
};

export const formatCurrency = (value: number, options: CurrencyOptions = {}) => {
  const { currency = "USD", minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export const formatCurrencyFromMinorUnits = (amount?: number | null, currency = "USD", fallback = "—") => {
  if (amount === null || amount === undefined) {
    return fallback;
  }

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  });
  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return formatter.format(amount / 10 ** fractionDigits);
};
